<template>
  <div class="w-full space-y-12 lg:space-y-20">
    <!-- Hero Detail -->
    <SectionsHeroDetail
      v-if="newsData?.heroDetail?.title"
      :key="'heroDetail'"
      :title="newsData?.heroDetail?.title"
      :description="newsData?.heroDetail?.description"
      :media="newsData?.heroDetail?.media"
      :backgroundColor="newsData?.heroDetail?.backgroundColor"
      :color="newsData?.heroDetail?.color"
      :breadcrumbs="breadcrumbs"
    />
    <!-- Hero Detail -->

    <section class="w-full overflow-hidden">
      <div class="container">
        <div
          class="grid grid-cols-1 lg:grid-cols-2 gap-6 2xl:gap-10"
          :class="newsData?.type === 'type_1' ? 'lg:grid-cols-3' : ''"
        >
          <template
            v-for="(item, index) in orderedNewsList"
            :key="item?.documentId || index"
          >
            <UiCardBlog
              :pin="item?.pin"
              :title="item?.title"
              :description="item?.description"
              :category="item?.category?.title"
              :date="formatDate(item?.createdAt)"
              :link="getNewsLink(item)"
              :media="item?.listMedia || item?.media"
              :type="newsData?.type"
            />
          </template>
        </div>
        <Paginator
          v-if="newsListMeta?.pagination?.pageCount > 1"
          :first="(selectedPage - 1) * pageSize"
          :rows="newsListMeta?.pagination?.pageSize"
          :totalRecords="newsListMeta?.pagination?.total"
          @page="onPageChange"
        />
      </div>
    </section>

    <SectionsSectionRenderer :sections="newsData?.sections" />
  </div>
</template>
<script setup>
definePageMeta({ name: "news" });

import { useApi } from "~/server/getApi";
import qs from "qs";
import { useI18n } from "vue-i18n";
import { useMetaTags } from "~/composables/headConfig";
import {
  buildJsonLdScripts,
  createWebPageSchema,
} from "~/composables/schemaOrg";
import Paginator from "primevue/paginator";
import { formatLocaleDate } from "~/composables/dateFormatter";
const { t, locale } = useI18n();

const route = useRoute();
const config = useRuntimeConfig();
const siteUrl = config.public.siteUrl;
const baseUrl = config.public.baseUrl;
const fullUrl = computed(() => `${siteUrl}${route.fullPath}`);
const selectedPage = ref(1);
const pageSize = ref(8);

const query = computed(() =>
  qs.stringify(
    {
      fields: ["title", "slug", "description"],
      populate: ["media"],
      sort: ["createdAt:asc"],
      pagination: {
        page: selectedPage.value,
        pageSize: pageSize.value,
      },
    },
    {
      encodeValuesOnly: true,
    },
  ),
);

const newsListEndpoint = computed(() => {
  let base = `news-lists?${query.value}`;
  return base;
});

const { data: newsData, fetchData: fetchNewsData } =
  useApi(`news?pLevel`);

await fetchNewsData();

const {
  data: newsListData,
  meta: newsListMeta,
  fetchData: fetchNewsListData,
} = useApi(newsListEndpoint, true);

await fetchNewsListData();

const orderedNewsList = computed(() => {
  const list = newsListData.value || [];
  return [...list].sort((a, b) => {
    const firstDate = new Date(a?.createdAt || 0).getTime();
    const secondDate = new Date(b?.createdAt || 0).getTime();
    return firstDate - secondDate;
  });
});

/* ---------- Breadcrumb Tanımı ---------- */
const breadcrumbs = computed(() => [
  {
    enabled: true,
    link: locale.value === "tr" ? "/" : `/${locale.value}`,
    title: homeIconSvg,
  },
  {
    enabled: true,
    link: `/${t("Routes.News").toLowerCase()}`,
    title: t("Breadcrumb.News"),
  },
]);

function onPageChange(event) {
  selectedPage.value = event.page + 1; // PrimeVue'da page index 0'dan başlar
  pageSize.value = event.rows;
  // yeni query string ile yeniden fetch
  fetchNewsListData();
}

/* -------------------- 🔧 SEO Etiketi -------------------- */
const seoData = computed(() => newsData.value?.seo ?? {});
const pageTitle = computed(
  () => newsData.value?.heroDetail?.title || newsData.value?.title,
);
const pageDescription = computed(
  () =>
    newsData.value?.heroDetail?.description ||
    newsData.value?.description,
);

useMetaTags(seoData, pageTitle, pageDescription);

/* -------------------- 📤 UseHead Scheme -------------------- */
useHead(() => {
  const page = newsData.value;
  if (!page) {
    return { script: [] };
  }

  const scripts = buildJsonLdScripts([
    {
      key: "schema-news-index",
      schema: createWebPageSchema({
        type: "CollectionPage",
        url: fullUrl.value,
        name: page.heroDetail?.title || page.title,
        headline: page.heroDetail?.title || page.title,
        description:
          page.seo?.description ||
          page.heroDetail?.description ||
          page.description,
        publishedAt: page.publishedAt,
        updatedAt: page.updatedAt,
        keywords: page.seo?.keywords,
        baseUrl,
        mainEntityType: "NewsArticle",
      }),
    },
  ]);

  return {
    script: scripts,
  };
});
const getNewsLink = (item) => `/${t('Routes.News').toLowerCase()}/${item?.slug}`;
const formatDate = (value) => formatLocaleDate(value);

const homeIconSvg = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 md:w-5 md:h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955a1.125 1.125 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>';
</script>
